/**
 * CounterRatesDialog — one row per quoted deliverable, prefilled with the
 * creator's amount and editable, plus an "Add deliverable" picker sourced
 * from OUTREACH_DELIVERABLES_BY_PLATFORM (mirrors the BE's
 * DeliverableTypesForPlatform, ruling 54). Used by ThreadComposer's Counter
 * kind (this file) and reused as Task 25's agree-rate grid.
 */
import { useEffect, useId, useState, type ChangeEvent, type ClipboardEvent, type ReactNode } from 'react';
import type { OutreachQuote, OutreachRate } from '@discovr/types';
import { OUTREACH_DELIVERABLES_BY_PLATFORM, OUTREACH_CURRENCIES, UNSPECIFIED_DELIVERABLE, fmtDeliverableType, isMain, resolvePlatform } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { X } from '../../../design-system/icons';
import { fmtRate } from './QuoteBlock';
import { fmtAmountInput, rateAmountError } from './agreedMoney';
import {
  AMOUNT_PASTE_ERROR,
  amountFromInput,
  inputTypeOf,
  maskAmountKeystroke,
  maskAmountPaste,
  regroupAmountText,
  type AmountEditResult,
} from './amountInput';

type RateRow = OutreachRate & {
  quoted?: { amount: number; currency: string };
  /** What the admin typed; unset until they touch the field. */
  text?: string;
  /** The last pasted, dropped or autofilled text was unreadable and was not inserted. */
  pasteError?: boolean;
};

const rowError = (r: RateRow): string | null => {
  if (r.pasteError) return AMOUNT_PASTE_ERROR;
  if (r.amount === 0 && (r.text === undefined || r.text === '')) return null;
  return rateAmountError(r.amount, r.currency);
};

const shownAmountText = (r: RateRow): string => r.text ?? (r.amount ? fmtAmountInput(r.amount, r.currency) : '');

const withCurrency = (r: RateRow, currency: string): RateRow =>
  r.text === undefined ? { ...r, currency } : { ...r, currency, text: regroupAmountText(r.text, currency) };

const quoteRates = (quote?: OutreachQuote): RateRow[] =>
  (quote?.rates ?? []).map(({ deliverable, amount, currency }) => ({ deliverable, amount, currency }));
/** The creator's figure moves to the placeholder; the input starts empty for ours. */
const quotedAsPlaceholders = (quote?: OutreachQuote): RateRow[] =>
  (quote?.rates ?? []).map(({ deliverable, amount, currency }) => ({ deliverable, amount: 0, currency, quoted: { amount, currency } }));

export interface CounterRatesDialogProps {
  open: boolean;
  platform: string;
  quote?: OutreachQuote;
  /** Reopening after an earlier edit (ThreadComposer's "Edit rates") seeds
   *  from the in-progress rates instead of the original quote. */
  initial?: OutreachRate[];
  title?: string;
  description?: string;
  submitLabel?: string;
  busy?: boolean;
  error?: string;
  /** Counter: each quoted deliverable starts empty, the creator's amount shown as its placeholder. */
  quoteAsPlaceholder?: boolean;
  /** Agree rate (R17-44): a deal is one main deliverable plus add-ons. Mains cannot be added past the first, and a
   *  quote that covers several mains makes the admin pick the one the price is for. */
  singleMain?: boolean;
  /** Extra controls under the rate grid (the agree dialog's acknowledgement option). */
  extra?: ReactNode;
  /** The rates Submit would send, on every edit (the agree dialog's acknowledgement follows them). */
  onRatesChange?: (rates: OutreachRate[]) => void;
  onSubmit: (rates: OutreachRate[]) => void;
  onCancel: () => void;
}

const submittableRates = (rows: RateRow[], keepMain?: (r: RateRow) => boolean): OutreachRate[] =>
  rows
    .filter((r) => r.amount > 0 && (keepMain?.(r) ?? true))
    .map(({ deliverable, amount, currency }) => ({ deliverable, amount, currency }));

/** The rows are a radio group only while the admin has to pick one main (singleMain with several mains). */
function RowsWrap({ pick, value, onValueChange, children }: { pick: boolean; value: string; onValueChange: (v: string) => void; children: ReactNode }) {
  return pick ? (
    <RadioGroup value={value} onValueChange={onValueChange} aria-label="Main deliverable" className="gap-3">
      {children}
    </RadioGroup>
  ) : (
    <>{children}</>
  );
}

export function CounterRatesDialog({
  open,
  platform,
  quote,
  initial,
  title = 'Your counter-offer',
  description,
  submitLabel = 'Use these rates',
  busy = false,
  error,
  quoteAsPlaceholder = false,
  singleMain = false,
  extra,
  onRatesChange,
  onSubmit,
  onCancel,
}: CounterRatesDialogProps) {
  const seed = (): RateRow[] => {
    return initial ?? (quoteAsPlaceholder ? quotedAsPlaceholders(quote) : quoteRates(quote));
  };
  const [rows, setRows] = useState<RateRow[]>(seed);
  const [addingType, setAddingType] = useState('');
  const [mainPick, setMainPick] = useState('');

  // The dialog stays mounted between opens (ThreadComposer toggles `open`
  // rather than remounting it) — re-seed on every open->true transition so a
  // previous session's edit never bleeds into a fresh one. Adjusting state
  // during render (React's own pattern for "reset on prop change") instead
  // of an effect, so this doesn't cost an extra commit.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setRows(seed());
      setAddingType('');
      setMainPick('');
    }
  }

  const resolvedPlatform = resolvePlatform(platform) ?? 'instagram';
  const mainRows = singleMain ? rows.filter((r) => isMain(r.deliverable, resolvedPlatform)) : [];
  const pickMain = mainRows.length > 1;
  const pickedMain = pickMain && mainRows.some((r) => r.deliverable === mainPick) ? mainPick : '';
  // With several mains only the picked one goes out; add-ons always do.
  const keepMain = pickMain ? (r: RateRow) => !isMain(r.deliverable, resolvedPlatform) || r.deliverable === pickedMain : undefined;
  const outgoing = () => submittableRates(rows, keepMain);

  useEffect(() => {
    onRatesChange?.(submittableRates(rows, keepMain));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, onRatesChange, pickMain, pickedMain]);

  const uid = useId();
  const currency = rows[0]?.currency ?? quote?.rates[0]?.currency ?? 'INR';
  const currencies: string[] = (OUTREACH_CURRENCIES as readonly string[]).includes(currency) ? [...OUTREACH_CURRENCIES] : [currency, ...OUTREACH_CURRENCIES];
  const platformTypes = OUTREACH_DELIVERABLES_BY_PLATFORM[resolvedPlatform] ?? [];
  const available = platformTypes.filter((type) => !rows.some((r) => r.deliverable === type) && !(singleMain && mainRows.length > 0 && isMain(type, resolvedPlatform)));
  // A blank row is dropped on submit, so only a filled one still needs its deliverable picked.
  const rowErrors = rows.map(rowError);
  const hasRowError = rowErrors.some((e) => e !== null);
  const noAmount = rows.length > 0 && !hasRowError && rows.every((r) => !(r.amount > 0));
  const unbound = rows.some((r) => r.deliverable === UNSPECIFIED_DELIVERABLE && (r.amount > 0 || !r.quoted));

  const updateRow = (index: number, change: Partial<RateRow>) => setRows((current) => current.map((r, i) => (i === index ? { ...r, ...change } : r)));
  // The field inserts its own grouping and ignores typed commas, so "18,50" can never be misread as 1850 or 18.50.
  // The masked text goes onto the element before the state update so React finds it unchanged and leaves the caret alone.
  // A rejected edit keeps what the admin typed next to the message (the amount is unreadable, so nothing can be sent).
  const applyAmountResult = (index: number, input: HTMLInputElement, attempted: string, result: AmountEditResult) => {
    if (result.kind === 'rejected') {
      input.value = attempted;
      updateRow(index, { text: attempted, amount: Number.NaN, pasteError: true });
      return;
    }
    const { text, caret } = result.edit;
    input.value = text;
    input.setSelectionRange(caret, caret);
    updateRow(index, { text, amount: amountFromInput(text), pasteError: false });
  };
  const handleAmountChange = (index: number, rate: RateRow, event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const previous = shownAmountText(rate);
    const result = maskAmountKeystroke({
      previous,
      value: input.value,
      caret: input.selectionStart ?? input.value.length,
      inputType: inputTypeOf(event.nativeEvent),
      currency: rate.currency,
    });
    applyAmountResult(index, input, input.value, result);
  };
  const handleAmountPaste = (index: number, rate: RateRow, event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const input = event.currentTarget;
    const previous = input.value;
    const selectionStart = input.selectionStart ?? previous.length;
    const selectionEnd = input.selectionEnd ?? previous.length;
    const pasted = event.clipboardData.getData('text');
    const result = maskAmountPaste({ current: previous, selectionStart, selectionEnd, pasted, currency: rate.currency });
    applyAmountResult(index, input, `${previous.slice(0, selectionStart)}${pasted}${previous.slice(selectionEnd)}`, result);
  };
  const setDeliverable = (index: number, deliverable: string) => updateRow(index, { deliverable });
  const setCurrency = (next: string) => setRows((current) => current.map((r) => withCurrency(r, next)));
  const removeRow = (index: number) => setRows((current) => current.filter((_, i) => i !== index));
  const addDeliverable = () => {
    if (!addingType) return;
    setRows((current) => [...current, { deliverable: addingType, amount: 0, currency }]);
    setAddingType('');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : <DialogDescription className="sr-only">Set the rates for this offer.</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor={`${uid}-currency`}>Currency</Label>
            <select id={`${uid}-currency`} value={currency} onChange={(e) => setCurrency(e.target.value)} className="h-9 rounded-lg border border-border bg-card px-2 text-sm">
              {currencies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          {pickMain ? (
            <p className="text-xs text-muted-foreground">
              A deal has one main deliverable plus add-ons. This quote covers several mains, so pick the one the price is for. The other mains are left out.
            </p>
          ) : null}
          {rows.length === 0 && <p className="text-sm text-muted-foreground">Add at least one deliverable to continue.</p>}
          <RowsWrap pick={pickMain} value={pickedMain} onValueChange={setMainPick}>
          {rows.map((rate, index) => {
            const id = `counter-rate-${rate.deliverable}-${index}`;
            const amountError = rowErrors[index];
            const describedBy = [rate.quoted ? `${id}-quoted` : null, amountError ? `${id}-error` : null].filter(Boolean).join(' ') || undefined;
            return (
              <div key={`${rate.deliverable}-${index}`}>
                <div className="flex items-center gap-2">
                  {pickMain && isMain(rate.deliverable, resolvedPlatform) ? (
                    <RadioGroupItem value={rate.deliverable} id={`${id}-main`} aria-label={`Use ${fmtDeliverableType(rate.deliverable)} as the main deliverable`} />
                  ) : null}
                  {rate.deliverable === UNSPECIFIED_DELIVERABLE ? (
                    <select
                      aria-label={`Deliverable for ${rate.quoted ? fmtRate(rate.quoted.amount, rate.quoted.currency) : fmtRate(rate.amount, rate.currency)}`}
                      value=""
                      onChange={(e) => setDeliverable(index, e.target.value)}
                      className="h-9 min-w-0 flex-1 rounded-lg border border-amber-300 bg-card px-2 text-sm"
                    >
                      <option value="">Pick a deliverable</option>
                      {platformTypes
                        .filter((t) => !rows.some((r) => r.deliverable === t))
                        .map((t) => (
                          <option key={t} value={t}>
                            {fmtDeliverableType(t)}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <div className="min-w-0 flex-1">
                      <Label htmlFor={id} className="block truncate">
                        {fmtDeliverableType(rate.deliverable)}
                      </Label>
                    </div>
                  )}
                  <Input
                    id={id}
                    aria-label={rate.deliverable === UNSPECIFIED_DELIVERABLE ? `Amount for ${rate.quoted ? fmtRate(rate.quoted.amount, rate.quoted.currency) : fmtRate(rate.amount, rate.currency)}` : undefined}
                    placeholder={rate.quoted ? fmtRate(rate.quoted.amount, rate.quoted.currency) : undefined}
                    aria-describedby={describedBy}
                    aria-invalid={amountError ? true : undefined}
                    inputMode="decimal"
                    type="text"
                    className="w-36"
                    value={shownAmountText(rate)}
                    onChange={(event) => handleAmountChange(index, rate, event)}
                    onPaste={(event) => handleAmountPaste(index, rate, event)}
                    onBlur={() => {
                      if (rate.text !== undefined && rate.amount > 0 && Number.isFinite(rate.amount)) {
                        updateRow(index, { text: fmtAmountInput(rate.amount, rate.currency) });
                      }
                    }}
                  />
                  {rate.quoted && (
                    <span id={`${id}-quoted`} className="sr-only">
                      {`Creator quoted ${fmtRate(rate.quoted.amount, rate.quoted.currency)}`}
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label={`Remove ${fmtDeliverableType(rate.deliverable)}`}
                    className="inline-flex size-6 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => removeRow(index)}
                  >
                    <X size={14} />
                  </button>
                </div>
              {amountError ? (
                <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-destructive">
                  {amountError}
                </p>
              ) : null}
              </div>
            );
          })}
          </RowsWrap>
          {available.length > 0 && (
            <div className="flex items-center gap-2">
              <Select value={addingType} onValueChange={setAddingType}>
                <SelectTrigger aria-label="Deliverable to add" className="flex-1">
                  <SelectValue placeholder="Choose a type" />
                </SelectTrigger>
                <SelectContent>
                  {available.map((type) => (
                    <SelectItem key={type} value={type}>
                      {fmtDeliverableType(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" disabled={!addingType} onClick={addDeliverable}>
                Add deliverable
              </Button>
            </div>
          )}
          {noAmount ? <p className="text-xs text-amber-700">Enter an amount for at least one deliverable.</p> : null}
          {unbound ? <p className="text-xs text-amber-700">The creator gave an amount without saying what it is for — pick the deliverable.</p> : null}
        </div>
        {extra}
        {error ? (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy || unbound || hasRowError || (pickMain && !pickedMain) || outgoing().length === 0}
            onClick={() => onSubmit(outgoing())}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
